'use client';

import {
  useApolloClient,
  useReactiveVar,
  useSuspenseQuery,
} from '@apollo/client';
import ThemeProviderWrapper from '@storefront/app/ThemeProviderWrapper';
import {
  GetPdpStateDocument,
  GetPdpStateQuery,
  GetSkuProductsDocument,
  GetSkuProductsQuery,
} from '@storefront/data-commerce';
import currency from 'currency.js';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from '@storefront/utils/priceHandler';

import styles from './order-details-scroll-bar.module.scss';
import { ContinueButton } from '../continue-button/continue-button';
import { BundleProductWithLowestPrice } from '../product-configuration/product-configuration.utils';
import { configurationState } from '../state-management/pdp-config-state';
import { getSkuList } from '../utils/utils';
import { NamespacesI18n } from '@storefront/i18n';
import { getProductsLowestPrice } from '@storefront/utils/utils';

interface OrderDetailsScrollBarProps {
  isContinueButtonDisabled: boolean;
  loading: boolean;
  isLoading: (loading: boolean) => void;
  hasTriggeredEvent: boolean;
  setHasTriggeredEvent: (value: boolean) => void;
  currencyCode?: string;
}

export default function OrderDetailsScrollBar({
  isContinueButtonDisabled,
  loading,
  isLoading,
  hasTriggeredEvent,
  setHasTriggeredEvent,
}: Readonly<OrderDetailsScrollBarProps>) {
  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG);
  const configuration = useReactiveVar(configurationState);
  const skuList = getSkuList(useSearchParams()?.get('skus'));
  const client = useApolloClient();

  const {
    pdpConfig: { prices, selected_options },
  } = client.readQuery<GetPdpStateQuery>({
    query: GetPdpStateDocument,
    variables: { skuList, configuration },
  });

  const {
    data: { products },
  } = useSuspenseQuery<GetSkuProductsQuery>(GetSkuProductsDocument, {
    variables: { skuList },
  });

  const currencyFromProducts =
    products?.items?.[0]?.price_range?.minimum_price?.regular_price?.currency ||
    'USD';

  const handlePrice = () => {
    if (selected_options?.length > 0) {
      return currency(prices?.totalPricePerMonth, {
        pattern: '!#',
        symbol: getCurrencySymbol(currencyFromProducts),
        precision: 2,
      }).format();
    } else {
      return currency(
        getProductsLowestPrice(
          products?.items as BundleProductWithLowestPrice[]
        ),
        {
          pattern: '!#',
          symbol: getCurrencySymbol(currencyFromProducts),
          precision: 2,
        }
      ).format();
    }
  };

  return (
    <div className={styles['container']}>
      <div className={styles['pdp-footer']}>
        <div className={styles['need-help']}>
          <span className={styles['text-bold']}>
            {t('pdpConfigFooterWithPrice.needHelp')}
          </span>
          <span className={styles['text-light']}>
            {t('pdpConfigFooterWithPrice.contactUs')}
          </span>
        </div>
        <div className={styles['price-continue']}>
          <div className={styles['price']}>
            <span className={styles['price-text']}>
              {isContinueButtonDisabled &&
                t('pdpConfigFooterWithPrice.priceRange')}
              {!isContinueButtonDisabled &&
                t('pdpConfigFooterWithPrice.priceTotal')}
            </span>
            <span className={styles['price-text']} data-testid="total-price">
              {t('pdpConfigFooterWithPrice.price', {
                price: handlePrice(),
              })}
            </span>
          </div>
          <div className={styles['continue-button']}>
            <ThemeProviderWrapper>
              <ContinueButton
                isDisabled={isContinueButtonDisabled}
                loading={loading}
                isLoading={isLoading}
                hasTriggeredEvent={hasTriggeredEvent}
                setHasTriggeredEvent={setHasTriggeredEvent}
              />
            </ThemeProviderWrapper>
          </div>
        </div>
      </div>
    </div>
  );
}
